import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import Body from 'storybookConfig/stories/molecules/Body';
import CopyTextButton from 'storybookConfig/stories/molecules/Button/CopyTextButton';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Input from 'storybookConfig/stories/molecules/Input';

const KeyInput = styled(Input)`
  font-family: ${({ theme }) => theme.font.monospace};
`;

const Command = styled.code`
  display: block;
  padding: 12px;
  border-radius: 8px;
  font-family: ${({ theme }) => theme.font.monospace};
  background: ${({ theme }) => theme.color.surfaceSubtle};
  white-space: pre-wrap;
  word-break: break-all;
`;

export interface McpKeyGeneratorProps {
  /**
   * API base URL the MCP server lives under
   */
  apiURL: string;
  /**
   * The raw key, only available right after generating
   */
  mcpKey?: string;
  isLoading?: boolean;
  onGenerate: () => void;
}

/**
 * Lets a super user mint their admin MCP key and shows the Claude Code setup command.
 */
const McpKeyGenerator = ({ apiURL, mcpKey, isLoading, onGenerate }: McpKeyGeneratorProps) => {
  const command = `claude mcp add --transport http md-admin ${apiURL}/admin/mcp --header "Authorization: Bearer ${mcpKey}"`;

  return (
    <Flex column gap="16px">
      <Body as="p">
        Connect Claude Code to the admin MCP server. Generating a new key revokes your current one.
      </Body>

      <Flex>
        <PrimaryButton
          size="small"
          $iconName="key-round"
          onClick={onGenerate}
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? 'Generating…' : 'Generate MCP key'}
        </PrimaryButton>
      </Flex>

      {mcpKey && (
        <>
          <Body variant="Body/Regular Bold" color="errorBase">
            Copy this key now. You won&apos;t be able to see it again.
          </Body>
          <Flex gap="8px" alignItemsCenter>
            <KeyInput value={mcpKey} readOnly isFullWidth aria-label="MCP key" />
            <CopyTextButton variant="secondary" size="small" text={mcpKey}>
              Copy key
            </CopyTextButton>
          </Flex>

          <Body variant="Body/Regular Bold">Set up Claude Code</Body>
          <Command>{command}</Command>
          <Flex>
            <CopyTextButton variant="secondary" size="small" text={command}>
              Copy command
            </CopyTextButton>
          </Flex>
          <Body as="p">
            Run <code>/mcp</code> in Claude Code. You should see <code>customer_lookup</code>.
          </Body>
          <Body as="p">Try: &quot;look up customer &lt;name&gt; on MD&quot;.</Body>
        </>
      )}
    </Flex>
  );
};

export default McpKeyGenerator;
